import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmDialog from '../../components/confirm-dialog';
import { useNavigationBlocker } from '../../hooks';
import {
  BuildingRequest,
  BuildingWithTrusses,
  GableTruss,
  HeaderTruss,
  LeanToTruss,
  OrderStatus,
  TrussRequest,
} from '../../types';
import { getErrorMessage, showToast, sumPrices } from '../../utils';
import AddBuildingDialog from './add-building-dialog';
import {
  postSizeDisplayToEnum,
  postSizeEnumToDisplay,
  resolveBuildingPostSizeDisplay,
  WidthMeasurementReference,
} from './building-constants';
import { BuildingCarousel } from './components/building-carousel';
import { BuildingCartSummary } from './components/building-cart-summary';
import { BuildingFormData, BuildingInformationForm } from './components/building-information-form';
import {
  getBuildingAsceFieldsForSave,
  getBuildingDisplayLoadRatings,
  getOrderLevelBuildingAsceFields,
  shouldPromptOrderAsceUpdate,
} from './building-load-rating-utils';
import BuildingLoadRatingBar from './components/building-load-rating-bar';
import { TrussSectionsContainer } from './components/truss-sections-container';
import ConfirmLoadRatingDialog from './confirm-load-rating-dialog';
import {
  useAddBuildingToOrderMutation,
  useCloneBuildingMutation,
  useConfirmAllTrussStateChangesMutation,
  useConfirmTrussStateChangeMutation,
  useDeleteBuildingMutation,
  useGetAsceLoadRatingsMutation,
  useGetBuildingByIdQuery,
  useGetBuildingsByOrderIdQuery,
  useGetOrderByIdQuery,
  useUpdateBuildingMutation,
  useUpdateOrderMutation,
} from './orders-api-slice';
import {
  createEmptyTrussRequest,
  gableTrussToRequest,
  headerTrussToRequest,
  leanToTrussToRequest,
  mapClassificationToFrontend,
  validatePostSizeForTrusses,
  withRecalculatedInsidePostMeasurements,
} from './truss-utils';

const toBuildingScalarUpdate = (
  building: BuildingWithTrusses,
  orderId: number,
  overrides: Partial<BuildingRequest> = {}
): BuildingRequest => ({
  orderId,
  name: building.name,
  confirmedLoadRating: building.confirmedLoadRating ?? false,
  width: building.width,
  length: building.length,
  height: building.height,
  baySpacing: building.baySpacing,
  postSize: building.postSize,
  widthMeasurementReference: building.widthMeasurementReference,
  application: building.application,
  additionalNotes: building.additionalNotes,
  isPermanentDwelling: building.isPermanentDwelling,
  includePermanentDwellingWithPoleBarn: building.includePermanentDwellingWithPoleBarn,
  includePermanentDwellingForPartiallyClosedPoleBarn:
    building.includePermanentDwellingForPartiallyClosedPoleBarn,
  includePermanentDwellingForFullyEnclosedPoleBarn:
    building.includePermanentDwellingForFullyEnclosedPoleBarn,
  includePermanentDwellingWithNoMEP: building.includePermanentDwellingWithNoMEP,
  trussesProvided: false,
  ...overrides,
});

const ManageBuildingProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: orderIdParam } = useParams<{ id: string }>();
  const orderId = orderIdParam ? parseInt(orderIdParam, 10) : 0;

  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [addBuildingDialogOpen, setAddBuildingDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [buildingToDelete, setBuildingToDelete] = useState<number | null>(null);
  const [buildingFormData, setBuildingFormData] = useState<BuildingFormData | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [postSizeWarning, setPostSizeWarning] = useState<{
    show: boolean;
    message: string;
    acknowledged: boolean;
    source: 'buildingSave' | 'updateCart';
  }>({
    show: false,
    message: '',
    acknowledged: false,
    source: 'buildingSave',
  });

  // Local state for managing trusses (for bulk update)
  const [localTrusses, setLocalTrusses] = useState<{
    gable: GableTruss[];
    header: HeaderTruss[];
    leanTo: LeanToTruss[];
  }>({
    gable: [],
    header: [],
    leanTo: [],
  });

  const { data: order, isLoading: isLoadingOrder } = useGetOrderByIdQuery(orderId.toString(), {
    skip: !orderId,
  });

  const {
    data: buildings = [],
    isLoading: isLoadingBuildings,
    refetch: refetchBuildings,
  } = useGetBuildingsByOrderIdQuery(orderId.toString(), {
    skip: !orderId,
  });

  const { data: selectedBuilding } = useGetBuildingByIdQuery(selectedBuildingId?.toString() || '', {
    skip: !selectedBuildingId,
  });

  // Engineering fees are now included in the order response
  const engineeringFees = order?.engineeringFees;

  const isOrderDraft = order?.status === OrderStatus.DRAFT;

  const [addBuilding] = useAddBuildingToOrderMutation();
  const [deleteBuilding] = useDeleteBuildingMutation();
  const [cloneBuilding] = useCloneBuildingMutation();
  const [updateBuilding] = useUpdateBuildingMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [getAsceLoadRatings] = useGetAsceLoadRatingsMutation();
  const [confirmTrussStateChange] = useConfirmTrussStateChangeMutation();
  const [confirmAllStateChanges, { isLoading: isConfirmingAll }] =
    useConfirmAllTrussStateChangesMutation();
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [isCloningBuilding, setIsCloningBuilding] = useState(false);

  // ASCE error state for Permanent Dwelling toggle
  const [asceError, setAsceError] = useState<{
    show: boolean;
    pendingPermanentDwelling: boolean;
    isRetrying: boolean;
  }>({ show: false, pendingPermanentDwelling: false, isRetrying: false });

  // Order-level ASCE confirmation state (shown when building changes affect order wind/snow)
  const [confirmLoadRatingOpen, setConfirmLoadRatingOpen] = useState(false);
  const [pendingOrderAsceData, setPendingOrderAsceData] = useState<{
    windLoad: number;
    snowLoad: number;
    previousWindLoad: number;
    previousSnowLoad: number;
    pendingBuildingId?: number;
    asceRiskLevel?: number;
  } | null>(null);

  // Sort buildings by id ascending so they display in the order they were added
  const sortedBuildings = [...buildings].sort((a, b) => a.id - b.id);

  const cartTotal = sumPrices(buildings.map(b => b.totalPrice || 0));

  /**
   * Displays ASCE result toasts and prompts the user to update order-level load ratings
   * when the fetched values differ from the current order values.
   */
  const notifyAsceResultAndPromptIfChanged = (
    asceResult: { windLoadMph: number; snowLoadPsf: number; snowParseWarning?: boolean },
    riskLevel: number,
    verb: 'refreshed' | 'retrieved' = 'refreshed',
    pendingBuildingId?: number
  ) => {
    showToast(
      `ASCE load ratings ${verb} for Risk Category ${riskLevel === 2 ? 'II' : 'I'}: ` +
        `Wind ${asceResult.windLoadMph} mph, Snow ${asceResult.snowLoadPsf} psf`,
      'info'
    );
    if (asceResult.snowParseWarning) {
      showToast(
        'Snow load could not be determined from ASCE data for this location. Defaulting to 0 psf — please verify and override if needed.',
        'warning'
      );
    }
    if (order && shouldPromptOrderAsceUpdate(asceResult, order)) {
      setPendingOrderAsceData({
        windLoad: asceResult.windLoadMph,
        snowLoad: asceResult.snowLoadPsf,
        previousWindLoad: order.windLoad,
        previousSnowLoad: order.snowLoad,
        pendingBuildingId,
        asceRiskLevel: riskLevel,
      });
      setConfirmLoadRatingOpen(true);
    }
  };

  const hasUnconfirmedStateChanges = useMemo(() => {
    if (!selectedBuilding) return false;
    const allTrusses = [...localTrusses.gable, ...localTrusses.header, ...localTrusses.leanTo];
    return allTrusses.some(t => t.stateChangeConfirmation === false);
  }, [selectedBuilding, localTrusses]);

  const handleConfirmTrussStateChange = async (trussId: number) => {
    if (!order?.id) return;
    try {
      await confirmTrussStateChange({
        orderId: order.id,
        trussId,
        buildingId: selectedBuildingId ?? undefined,
      }).unwrap();

      setLocalTrusses(prev => ({
        gable: prev.gable.map(t =>
          t.id === trussId ? { ...t, stateChangeConfirmation: true } : t
        ),
        header: prev.header.map(t =>
          t.id === trussId ? { ...t, stateChangeConfirmation: true } : t
        ),
        leanTo: prev.leanTo.map(t =>
          t.id === trussId ? { ...t, stateChangeConfirmation: true } : t
        ),
      }));
      showToast('State change confirmed for truss', 'success');
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleConfirmAllStateChanges = async () => {
    if (!order?.id) return;
    try {
      await confirmAllStateChanges({
        orderId: order.id,
        buildingId: selectedBuildingId ?? undefined,
      }).unwrap();

      setLocalTrusses(prev => ({
        gable: prev.gable.map(t => ({ ...t, stateChangeConfirmation: true })),
        header: prev.header.map(t => ({ ...t, stateChangeConfirmation: true })),
        leanTo: prev.leanTo.map(t => ({ ...t, stateChangeConfirmation: true })),
      }));
      showToast('All state changes confirmed', 'success');
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    }
  };

  // Calculate selected building totals by truss type (using local trusses for real-time updates)
  const buildingCartDetails = React.useMemo(() => {
    if (!selectedBuilding) {
      return {
        total: 0,
        gableTotal: 0,
        headerTotal: 0,
        leanToTotal: 0,
      };
    }

    const gableTotal = sumPrices(localTrusses.gable.map(t => (t.price || 0) * (t.quantity || 1)));
    const headerTotal = sumPrices(localTrusses.header.map(t => (t.price || 0) * (t.quantity || 1)));
    const leanToTotal = sumPrices(localTrusses.leanTo.map(t => (t.price || 0) * (t.quantity || 1)));
    const total = sumPrices([gableTotal, headerTotal, leanToTotal]);

    return {
      total,
      gableTotal,
      headerTotal,
      leanToTotal,
    };
  }, [selectedBuilding, localTrusses]);

  const selectedBuildingLoadRatings = useMemo(() => {
    if (!selectedBuilding || !order) {
      return null;
    }
    return getBuildingDisplayLoadRatings(selectedBuilding, order);
  }, [selectedBuilding, order]);

  // Track server state for trusses (for comparison)
  const serverTrusses = useMemo(() => {
    if (!selectedBuilding) {
      return { gable: [], header: [], leanTo: [] };
    }
    return {
      gable: (selectedBuilding.gableTrusses || []).map(truss => ({
        ...truss,
        classification: mapClassificationToFrontend(truss.classification, 'Gable'),
      })),
      header: selectedBuilding.headerTrusses || [],
      leanTo: (selectedBuilding.leanToTrusses || []).map(truss => ({
        ...truss,
        classification: mapClassificationToFrontend(truss.classification, 'LeanTo'),
        postSizeOverrideValue: truss.postSizeOverrideValue
          ? postSizeEnumToDisplay[truss.postSizeOverrideValue] || truss.postSizeOverrideValue
          : truss.postSizeOverrideValue,
      })),
    };
  }, [selectedBuilding]);

  // Helper function to compare trusses (deep comparison)
  const trussesEqual = (
    truss1: GableTruss | HeaderTruss | LeanToTruss,
    truss2: GableTruss | HeaderTruss | LeanToTruss
  ): boolean => {
    return (
      truss1.id === truss2.id &&
      truss1.classification === truss2.classification &&
      truss1.sizeFeet === truss2.sizeFeet &&
      truss1.sizeInches === truss2.sizeInches &&
      truss1.quantity === truss2.quantity &&
      truss1.measurementInsidePost === truss2.measurementInsidePost &&
      truss1.confirmedMeasurementInsidePost === truss2.confirmedMeasurementInsidePost &&
      truss1.carriageBolts === truss2.carriageBolts &&
      truss1.price === truss2.price &&
      truss1.notes === truss2.notes &&
      (truss1.productType === 'Gable' && truss2.productType === 'Gable'
        ? truss1.roofPitch === truss2.roofPitch &&
          truss1.overhangType === truss2.overhangType &&
          truss1.overhangSize === truss2.overhangSize
        : true) &&
      (truss1.productType === 'LeanTo' && truss2.productType === 'LeanTo'
        ? truss1.roofPitch === truss2.roofPitch &&
          truss1.overhangType === truss2.overhangType &&
          truss1.overhangSize === truss2.overhangSize &&
          truss1.monoslopeTruss === truss2.monoslopeTruss &&
          truss1.postSizeOverride === truss2.postSizeOverride &&
          truss1.postSizeOverrideValue === truss2.postSizeOverrideValue &&
          truss1.connectToHeaderTruss === truss2.connectToHeaderTruss
        : true)
    );
  };

  // Check if trusses have changed from server state
  const hasTrussChanges = useMemo(() => {
    // Check if trusses have changed
    const gableChanged =
      localTrusses.gable.length !== serverTrusses.gable.length ||
      localTrusses.gable.some(
        (truss, index) =>
          !serverTrusses.gable[index] || !trussesEqual(truss, serverTrusses.gable[index])
      ) ||
      serverTrusses.gable.some(
        (truss, index) =>
          !localTrusses.gable[index] || !trussesEqual(truss, localTrusses.gable[index])
      );

    const headerChanged =
      localTrusses.header.length !== serverTrusses.header.length ||
      localTrusses.header.some(
        (truss, index) =>
          !serverTrusses.header[index] || !trussesEqual(truss, serverTrusses.header[index])
      ) ||
      serverTrusses.header.some(
        (truss, index) =>
          !localTrusses.header[index] || !trussesEqual(truss, localTrusses.header[index])
      );

    const leanToChanged =
      localTrusses.leanTo.length !== serverTrusses.leanTo.length ||
      localTrusses.leanTo.some(
        (truss, index) =>
          !serverTrusses.leanTo[index] || !trussesEqual(truss, serverTrusses.leanTo[index])
      ) ||
      serverTrusses.leanTo.some(
        (truss, index) =>
          !localTrusses.leanTo[index] || !trussesEqual(truss, localTrusses.leanTo[index])
      );

    return gableChanged || headerChanged || leanToChanged;
  }, [localTrusses, serverTrusses]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!selectedBuilding || !buildingFormData) return false;

    // Check if building form data has changed
    const buildingDataChanged =
      selectedBuilding.width !== buildingFormData.width ||
      selectedBuilding.length !== buildingFormData.length ||
      selectedBuilding.height !== buildingFormData.height ||
      selectedBuilding.baySpacing !== buildingFormData.baySpacing ||
      selectedBuilding.postSize !==
        (postSizeDisplayToEnum[buildingFormData.postSize] || buildingFormData.postSize) ||
      selectedBuilding.application !== buildingFormData.application ||
      selectedBuilding.additionalNotes !== buildingFormData.additionalNotes ||
      selectedBuilding.isPermanentDwelling !== buildingFormData.isPermanentDwelling ||
      selectedBuilding.includePermanentDwellingWithPoleBarn !==
        buildingFormData.includePermanentDwellingWithPoleBarn ||
      selectedBuilding.includePermanentDwellingForPartiallyClosedPoleBarn !==
        buildingFormData.includePermanentDwellingForPartiallyClosedPoleBarn ||
      selectedBuilding.includePermanentDwellingForFullyEnclosedPoleBarn !==
        buildingFormData.includePermanentDwellingForFullyEnclosedPoleBarn ||
      selectedBuilding.includePermanentDwellingWithNoMEP !==
        buildingFormData.includePermanentDwellingWithNoMEP;

    // Check if trusses have changed
    const gableChanged =
      localTrusses.gable.length !== serverTrusses.gable.length ||
      localTrusses.gable.some(
        (truss, index) =>
          !serverTrusses.gable[index] || !trussesEqual(truss, serverTrusses.gable[index])
      ) ||
      serverTrusses.gable.some(
        (truss, index) =>
          !localTrusses.gable[index] || !trussesEqual(truss, localTrusses.gable[index])
      );

    const headerChanged =
      localTrusses.header.length !== serverTrusses.header.length ||
      localTrusses.header.some(
        (truss, index) =>
          !serverTrusses.header[index] || !trussesEqual(truss, serverTrusses.header[index])
      ) ||
      serverTrusses.header.some(
        (truss, index) =>
          !localTrusses.header[index] || !trussesEqual(truss, localTrusses.header[index])
      );

    const leanToChanged =
      localTrusses.leanTo.length !== serverTrusses.leanTo.length ||
      localTrusses.leanTo.some(
        (truss, index) =>
          !serverTrusses.leanTo[index] || !trussesEqual(truss, serverTrusses.leanTo[index])
      ) ||
      serverTrusses.leanTo.some(
        (truss, index) =>
          !localTrusses.leanTo[index] || !trussesEqual(truss, localTrusses.leanTo[index])
      );

    return buildingDataChanged || gableChanged || headerChanged || leanToChanged;
  }, [selectedBuilding, buildingFormData, localTrusses, serverTrusses]);

  // Navigation blocker for unsaved changes
  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirmNavigation,
    handleCancelNavigation,
    allowNavigation,
  } = useNavigationBlocker(hasUnsavedChanges);

  // Auto-select first building if none selected
  useEffect(() => {
    if (!selectedBuildingId && sortedBuildings.length > 0) {
      setSelectedBuildingId(sortedBuildings[0].id);
    }
  }, [sortedBuildings, selectedBuildingId]);

  // Clear all state when all buildings are deleted
  useEffect(() => {
    if (sortedBuildings.length === 0) {
      setSelectedBuildingId(null);
      setBuildingFormData(null);
      setLocalTrusses({
        gable: [],
        header: [],
        leanTo: [],
      });
    }
  }, [sortedBuildings.length]);

  // Safety net: if the selected building was removed from the list (e.g. stale selectedBuildingId
  // after a delete race), clear local state and fall back to the first available building.
  useEffect(() => {
    if (
      selectedBuildingId !== null &&
      sortedBuildings.length > 0 &&
      !sortedBuildings.some(b => b.id === selectedBuildingId)
    ) {
      setBuildingFormData(null);
      setLocalTrusses({ gable: [], header: [], leanTo: [] });
      setSelectedBuildingId(sortedBuildings[0].id);
    }
  }, [sortedBuildings, selectedBuildingId]);

  // Initialize form data and trusses when building is selected
  useEffect(() => {
    if (selectedBuilding) {
      // Convert enum value from API (e.g., "SixBySix") to display value (e.g., "6x6")
      const postSizeDisplay =
        postSizeEnumToDisplay[selectedBuilding.postSize] || selectedBuilding.postSize;
      setBuildingFormData({
        width: selectedBuilding.width,
        length: selectedBuilding.length,
        height: selectedBuilding.height,
        baySpacing: selectedBuilding.baySpacing,
        postSize: postSizeDisplay,
        widthMeasurementReference:
          (selectedBuilding.widthMeasurementReference as WidthMeasurementReference) ||
          WidthMeasurementReference.OutsideOutside,
        application:
          selectedBuilding.application === 'Residential' ||
          selectedBuilding.application === 'Agricultural'
            ? selectedBuilding.application
            : 'Residential',
        additionalNotes: selectedBuilding.additionalNotes,
        isPermanentDwelling: selectedBuilding.isPermanentDwelling,
        includePermanentDwellingWithPoleBarn: selectedBuilding.includePermanentDwellingWithPoleBarn,
        includePermanentDwellingForPartiallyClosedPoleBarn:
          selectedBuilding.includePermanentDwellingForPartiallyClosedPoleBarn,
        includePermanentDwellingForFullyEnclosedPoleBarn:
          selectedBuilding.includePermanentDwellingForFullyEnclosedPoleBarn,
        includePermanentDwellingWithNoMEP: selectedBuilding.includePermanentDwellingWithNoMEP,
      });

      // Initialize trusses from building data and map classifications from backend enum to frontend display
      setLocalTrusses({
        gable: (selectedBuilding.gableTrusses || []).map(truss => ({
          ...truss,
          classification: mapClassificationToFrontend(truss.classification, 'Gable'),
        })),
        header: selectedBuilding.headerTrusses || [],
        leanTo: (selectedBuilding.leanToTrusses || []).map(truss => ({
          ...truss,
          classification: mapClassificationToFrontend(truss.classification, 'LeanTo'),
          postSizeOverrideValue: truss.postSizeOverrideValue
            ? postSizeEnumToDisplay[truss.postSizeOverrideValue] || truss.postSizeOverrideValue
            : truss.postSizeOverrideValue,
        })),
      });
    } else {
      setBuildingFormData(null);
      setLocalTrusses({ gable: [], header: [], leanTo: [] });
    }
  }, [selectedBuilding]);

  // Dismiss post-size warning dialog when the post size changes, since the
  // previous warning no longer applies to the updated configuration.
  // NOTE: localTrusses is intentionally excluded from the dependency array.
  // Including it caused a race condition in React 18's automatic batching:
  // when a truss change (e.g. confirming inside-post measurement) and an
  // "Update Cart" click were batched in the same render cycle, the warning
  // was set and immediately dismissed before the user could see it.
  // If a user removes or resizes the offending truss while the dialog is open
  // they can dismiss it manually via the Cancel button.
  useEffect(() => {
    setPostSizeWarning(prev =>
      prev.show || prev.acknowledged
        ? { show: false, message: '', acknowledged: false, source: prev.source }
        : prev
    );
  }, [buildingFormData?.postSize]);

  /**
   * Handles updating a building's name
   * @param buildingId - The ID of the building to update
   * @param newName - The new name for the building
   * @throws Error if building is not found or update fails
   */
  const handleUpdateBuildingName = async (buildingId: number, newName: string) => {
    try {
      setIsUpdatingName(true);
      const building = buildings.find(b => b.id === buildingId);
      if (!building) {
        const error = new Error(`Building with ID ${buildingId} not found`);
        console.error('Building not found:', buildingId);
        throw error;
      }

      // If updating the selected building, use selectedBuilding data (more complete)
      // Otherwise use building from the list
      const buildingToUpdate = buildingId === selectedBuilding?.id ? selectedBuilding : building;

      // Get confirmedLoadRating from the appropriate building object
      // Both BuildingWithTrusses and BuildingWithoutTrusses now have this property
      const confirmedLoadRating =
        buildingId === selectedBuilding?.id
          ? (selectedBuilding.confirmedLoadRating ?? false)
          : (building.confirmedLoadRating ?? false);

      await updateBuilding({
        buildingId: buildingId,
        building: {
          orderId: orderId,
          name: newName,
          confirmedLoadRating: confirmedLoadRating,
          width: buildingToUpdate.width,
          length: buildingToUpdate.length,
          height: buildingToUpdate.height,
          baySpacing: buildingToUpdate.baySpacing,
          postSize: buildingToUpdate.postSize, // Already in correct format from API
          widthMeasurementReference: buildingToUpdate.widthMeasurementReference,
          application: buildingToUpdate.application,
          additionalNotes: buildingToUpdate.additionalNotes,
          isPermanentDwelling: buildingToUpdate.isPermanentDwelling,
          includePermanentDwellingWithPoleBarn:
            buildingToUpdate.includePermanentDwellingWithPoleBarn,
          includePermanentDwellingForPartiallyClosedPoleBarn:
            buildingToUpdate.includePermanentDwellingForPartiallyClosedPoleBarn,
          includePermanentDwellingForFullyEnclosedPoleBarn:
            buildingToUpdate.includePermanentDwellingForFullyEnclosedPoleBarn,
          includePermanentDwellingWithNoMEP: buildingToUpdate.includePermanentDwellingWithNoMEP,
          trussesProvided: false,
        },
      }).unwrap();

      // Refetch buildings to get updated name
      await refetchBuildings();
      showToast('Building name updated successfully', 'success');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update building name';
      console.error('Failed to update building name:', error);
      showToast(errorMessage, 'error');
      throw error; // Re-throw to let EditableBuildingName handle it
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleSaveBuildingChanges = async (
    formDataOverride?: BuildingFormData,
    skipPostSizeWarning = false
  ) => {
    // Use the form data passed directly, or fall back to state if not provided
    const dataToSave = formDataOverride || buildingFormData;
    if (!selectedBuilding || !dataToSave) return;

    try {
      // VALIDATION: Check 6x6 post-size requirements per truss classification
      if (!skipPostSizeWarning) {
        const validationResult = validatePostSizeForTrusses(dataToSave.postSize, localTrusses);

        if (!validationResult.isValid) {
          setPostSizeWarning({
            show: true,
            message: validationResult.warningMessage,
            acknowledged: false,
            source: 'buildingSave',
          });
          return;
        }
      }

      // Convert display value (e.g., "6x6") to enum value (e.g., "SixBySix") for API
      const postSizeEnum = postSizeDisplayToEnum[dataToSave.postSize] || dataToSave.postSize;

      // If Permanent Dwelling changed, re-call ASCE with the new riskLevel
      const permanentDwellingChanged =
        !!dataToSave.isPermanentDwelling !== selectedBuilding.isPermanentDwelling;
      let asceFields: {
        asceWindLoadMph?: number;
        asceSnowLoadPsf?: number;
        asceRiskLevel?: number;
      } = {};

      if (
        permanentDwellingChanged &&
        order?.jobLocation?.latitude &&
        order?.jobLocation?.longitude
      ) {
        const newRiskLevel = dataToSave.isPermanentDwelling ? 2 : 1;
        try {
          const asceResult = await getAsceLoadRatings({
            lat: order.jobLocation.latitude,
            lon: order.jobLocation.longitude,
            riskLevel: newRiskLevel,
          }).unwrap();
          asceFields = getBuildingAsceFieldsForSave(asceResult, order, newRiskLevel);
          notifyAsceResultAndPromptIfChanged(
            asceResult,
            newRiskLevel,
            'refreshed',
            selectedBuilding.id
          );
        } catch {
          setAsceError({
            show: true,
            pendingPermanentDwelling: !!dataToSave.isPermanentDwelling,
            isRetrying: false,
          });
          return;
        }
      }

      await updateBuilding({
        buildingId: selectedBuilding.id,
        building: {
          orderId: orderId,
          name: selectedBuilding.name,
          confirmedLoadRating: selectedBuilding.confirmedLoadRating ?? false,
          width: dataToSave.width,
          length: dataToSave.length,
          height: dataToSave.height,
          baySpacing: dataToSave.baySpacing,
          postSize: postSizeEnum,
          widthMeasurementReference:
            dataToSave.widthMeasurementReference ?? WidthMeasurementReference.OutsideOutside,
          application: dataToSave.application,
          additionalNotes: dataToSave.additionalNotes,
          isPermanentDwelling: dataToSave.isPermanentDwelling ?? undefined,
          includePermanentDwellingWithPoleBarn:
            dataToSave.includePermanentDwellingWithPoleBarn ?? undefined,
          includePermanentDwellingForPartiallyClosedPoleBarn:
            dataToSave.includePermanentDwellingForPartiallyClosedPoleBarn ?? undefined,
          includePermanentDwellingForFullyEnclosedPoleBarn:
            dataToSave.includePermanentDwellingForFullyEnclosedPoleBarn ?? undefined,
          includePermanentDwellingWithNoMEP:
            dataToSave.includePermanentDwellingWithNoMEP ?? undefined,
          ...asceFields,
          trussesProvided: false,
        },
      }).unwrap();

      // Reset warning state after successful save
      setPostSizeWarning(prev => ({
        show: false,
        message: '',
        acknowledged: false,
        source: prev.source,
      }));

      // Allow navigation after successful save (clears unsaved changes warning)
      allowNavigation();
      setFormResetKey(prev => prev + 1);
      showToast('Building updated successfully', 'success');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showToast(errorMessage, 'error');
    }
  };

  const handleAddBuilding = async (building: BuildingRequest) => {
    try {
      setIsSubmitting(true);

      if (order?.jobLocation?.latitude && order?.jobLocation?.longitude) {
        const riskLevel = building.isPermanentDwelling ? 2 : 1;

        if (order.manualLoadRatingCertified) {
          building = {
            ...building,
            ...getOrderLevelBuildingAsceFields(order, riskLevel),
          };
        } else {
          try {
            const asceResult = await getAsceLoadRatings({
              lat: order.jobLocation.latitude,
              lon: order.jobLocation.longitude,
              riskLevel,
            }).unwrap();
            building = {
              ...building,
              ...getBuildingAsceFieldsForSave(asceResult, order, riskLevel),
            };

            const newBuilding = await addBuilding({ orderId, building }).unwrap();
            await refetchBuildings();
            if (newBuilding?.id) {
              setSelectedBuildingId(newBuilding.id);
            }
            notifyAsceResultAndPromptIfChanged(asceResult, riskLevel, 'retrieved', newBuilding?.id);
            showToast('Building added successfully', 'success');
            return;
          } catch {
            showToast(
              'Unable to retrieve ASCE load ratings for this building. Using order-level values.',
              'warning'
            );
          }
        }
      }

      const newBuilding = await addBuilding({ orderId, building }).unwrap();
      await refetchBuildings();
      if (newBuilding?.id) {
        setSelectedBuildingId(newBuilding.id);
      }
      showToast('Building added successfully', 'success');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showToast(errorMessage, 'error');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (buildingId: number) => {
    setBuildingToDelete(buildingId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!buildingToDelete) return;

    try {
      if (selectedBuildingId === buildingToDelete) {
        // Compute next selection from the current list before the async delete + refetch
        // so the auto-select effect never runs with a stale list that still contains
        // the deleted building (which would re-select it and leave ghost totals on screen).
        const remainingBuildings = sortedBuildings.filter(b => b.id !== buildingToDelete);
        setSelectedBuildingId(remainingBuildings[0]?.id ?? null);
        // Eagerly clear local state so totals show 0 immediately rather than persisting
        // the deleted building's values until the RTK Query cache invalidates.
        setBuildingFormData(null);
        setLocalTrusses({ gable: [], header: [], leanTo: [] });
      }
      await deleteBuilding(buildingToDelete).unwrap();
      await refetchBuildings();
      setDeleteDialogOpen(false);
      setBuildingToDelete(null);
      showToast('Building deleted successfully', 'success');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showToast(errorMessage, 'error');
      setDeleteDialogOpen(false);
      setBuildingToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBuildingToDelete(null);
  };

  const handleConfirmOrderLoadRatingUpdate = async () => {
    if (!order?.id || !pendingOrderAsceData) return;

    try {
      await updateOrder({
        ...order,
        windLoad: pendingOrderAsceData.windLoad,
        snowLoad: pendingOrderAsceData.snowLoad,
        confirmedLoadRating: true,
        id: order.id,
      }).unwrap();

      if (
        pendingOrderAsceData.pendingBuildingId != null &&
        pendingOrderAsceData.asceRiskLevel != null
      ) {
        const buildingToUpdate = buildings.find(
          b => b.id === pendingOrderAsceData.pendingBuildingId
        );
        if (buildingToUpdate) {
          await updateBuilding({
            buildingId: buildingToUpdate.id,
            building: toBuildingScalarUpdate(buildingToUpdate, orderId, {
              asceWindLoadMph: pendingOrderAsceData.windLoad,
              asceSnowLoadPsf: pendingOrderAsceData.snowLoad,
              asceRiskLevel: pendingOrderAsceData.asceRiskLevel,
            }),
          }).unwrap();
          await refetchBuildings();
        }
      }

      setConfirmLoadRatingOpen(false);
      setPendingOrderAsceData(null);
      showToast('Order wind and snow load ratings have been updated', 'success');
    } catch {
      showToast('Failed to update order-level load ratings', 'error');
    }
  };

  const handleCloneBuilding = async (buildingId: number) => {
    if (isCloningBuilding) return;
    setIsCloningBuilding(true);
    try {
      const clonedBuilding = await cloneBuilding({
        sourceBuildingId: buildingId,
        targetOrderId: orderId,
      }).unwrap();
      await refetchBuildings();
      setSelectedBuildingId(clonedBuilding.id);
      showToast('Building cloned successfully', 'success');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showToast(errorMessage, 'error');
    } finally {
      setIsCloningBuilding(false);
    }
  };

  const handleUpdateCart = async (skipPostSizeWarning = false) => {
    if (!selectedBuilding || !buildingFormData) return;

    // Prevent multiple simultaneous calls
    if (isSubmitting) return;

    // Validate: Header truss cannot be saved unless gable or lean-to truss exists
    const hasHeaderTrusses = localTrusses.header.length > 0;
    const hasGableOrLeanToTruss = localTrusses.gable.length > 0 || localTrusses.leanTo.length > 0;

    if (hasHeaderTrusses && !hasGableOrLeanToTruss) {
      showToast(
        'Header truss cannot be saved unless a gable or lean-to truss is added first.',
        'error'
      );
      return;
    }

    // Validate that all trusses have confirmed measurement inside post
    const buildingPostSize = resolveBuildingPostSizeDisplay(
      buildingFormData.postSize,
      selectedBuilding.postSize
    );
    const trussesForSave = withRecalculatedInsidePostMeasurements(localTrusses, buildingPostSize);
    const allTrusses = [
      ...trussesForSave.gable,
      ...trussesForSave.header,
      ...trussesForSave.leanTo,
    ];
    const unconfirmedTrusses = allTrusses.filter(truss => !truss.confirmedMeasurementInsidePost);

    if (unconfirmedTrusses.length > 0) {
      showToast(
        'Please confirm the inside post to post measurement for all trusses before updating the cart.',
        'error'
      );
      return;
    }

    // Validate that no truss has an invalid inside measurement (posts wider than span).
    if (allTrusses.some(truss => truss.measurementInsidePost <= 0)) {
      showToast(
        'One or more trusses have an inside post-to-post measurement that is invalid. ' +
          'The posts are too wide for the selected truss span.',
        'error'
      );
      return;
    }

    // VALIDATION: Check post size requirements for trusses >= 50 feet
    if (!skipPostSizeWarning) {
      const validationResult = validatePostSizeForTrusses(
        buildingFormData.postSize,
        trussesForSave
      );

      if (!validationResult.isValid) {
        setPostSizeWarning({
          show: true,
          message: validationResult.warningMessage,
          acknowledged: false,
          source: 'updateCart',
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);

      // Persist recalculated measurements so cart state matches what we save
      setLocalTrusses(trussesForSave);

      // Convert all trusses to TrussRequest format
      const orderDate = order?.orderDate || new Date().toISOString();
      const trussRequests: TrussRequest[] = [
        ...trussesForSave.gable.map(truss => gableTrussToRequest(truss, orderDate)),
        ...trussesForSave.header.map(truss => headerTrussToRequest(truss, orderDate)),
        ...trussesForSave.leanTo.map(truss => leanToTrussToRequest(truss, orderDate)),
      ];

      // Convert display value (e.g., "6x6") to enum value (e.g., "SixBySix") for API
      const postSizeEnum =
        postSizeDisplayToEnum[buildingFormData.postSize] || buildingFormData.postSize;

      await updateBuilding({
        buildingId: selectedBuilding.id,
        building: {
          orderId: orderId,
          name: selectedBuilding.name,
          confirmedLoadRating: selectedBuilding.confirmedLoadRating ?? false,
          width: buildingFormData.width,
          length: buildingFormData.length,
          height: buildingFormData.height,
          baySpacing: buildingFormData.baySpacing,
          postSize: postSizeEnum,
          widthMeasurementReference:
            buildingFormData.widthMeasurementReference ?? WidthMeasurementReference.OutsideOutside,
          application: buildingFormData.application,
          additionalNotes: buildingFormData.additionalNotes,
          isPermanentDwelling: buildingFormData.isPermanentDwelling,
          includePermanentDwellingWithPoleBarn:
            buildingFormData.includePermanentDwellingWithPoleBarn ?? undefined,
          includePermanentDwellingForPartiallyClosedPoleBarn:
            buildingFormData.includePermanentDwellingForPartiallyClosedPoleBarn ?? undefined,
          includePermanentDwellingForFullyEnclosedPoleBarn:
            buildingFormData.includePermanentDwellingForFullyEnclosedPoleBarn ?? undefined,
          includePermanentDwellingWithNoMEP:
            buildingFormData.includePermanentDwellingWithNoMEP ?? undefined,
          trusses: trussRequests,
          trussesProvided: true,
        },
      }).unwrap();

      // Reset warning state after successful cart update
      setPostSizeWarning(prev => ({
        show: false,
        message: '',
        acknowledged: false,
        source: prev.source,
      }));

      // The building list and detail queries refetch automatically via cache
      // invalidation (see updateBuilding.invalidatesTags), so no explicit refetch here.
      // Allow navigation after successful save (clears unsaved changes warning)
      allowNavigation();
      setFormResetKey(prev => prev + 1);
      showToast('Cart updated successfully', 'success');
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveFromCart = () => {
    if (!selectedBuilding) return;
    handleDeleteClick(selectedBuilding.id);
  };

  // Truss management handlers
  const handleAddGableTruss = () => {
    if (!order) return;
    const newTruss = createEmptyTrussRequest('Gable', order.orderDate || new Date().toISOString());
    // Create a temporary GableTruss object for local state
    const gableTruss: GableTruss = {
      id: 0, // New truss
      buildingId: selectedBuilding?.id || 0,
      productType: 'Gable',
      classification: newTruss.classification,
      sizeFeet: newTruss.sizeFeet,
      sizeInches: newTruss.sizeInches,
      quantity: newTruss.quantity,
      measurementInsidePost: 0,
      confirmedMeasurementInsidePost: false,
      carriageBolts: false,
      price: 0,
      roofPitch: newTruss.roofPitch || '4:12',
      overhangType: newTruss.overhangType || 'Standard',
    };
    setLocalTrusses(prev => ({
      ...prev,
      gable: [...prev.gable, gableTruss],
    }));
  };

  const handleAddHeaderTruss = () => {
    if (!order) return;

    // Validate: Header truss cannot be added unless gable or lean-to truss exists
    const hasGableOrLeanToTruss = localTrusses.gable.length > 0 || localTrusses.leanTo.length > 0;

    if (!hasGableOrLeanToTruss) {
      showToast(
        'Header truss cannot be added unless a gable or lean-to truss is added first.',
        'error'
      );
      return;
    }

    const newTruss = createEmptyTrussRequest('Header', order.orderDate || new Date().toISOString());
    const headerTruss: HeaderTruss = {
      id: 0, // New truss
      buildingId: selectedBuilding?.id || 0,
      productType: 'Header',
      classification: newTruss.classification,
      sizeFeet: newTruss.sizeFeet,
      sizeInches: newTruss.sizeInches,
      quantity: newTruss.quantity,
      measurementInsidePost: 0,
      confirmedMeasurementInsidePost: false,
      carriageBolts: false,
      price: 0,
    };
    setLocalTrusses(prev => ({
      ...prev,
      header: [...prev.header, headerTruss],
    }));
  };

  const handleAddLeanToTruss = () => {
    if (!order) return;
    const newTruss = createEmptyTrussRequest('LeanTo', order.orderDate || new Date().toISOString());
    const leanToTruss: LeanToTruss = {
      id: 0, // New truss
      buildingId: selectedBuilding?.id || 0,
      productType: 'LeanTo',
      classification: newTruss.classification,
      sizeFeet: newTruss.sizeFeet,
      sizeInches: newTruss.sizeInches,
      quantity: newTruss.quantity,
      measurementInsidePost: 0,
      confirmedMeasurementInsidePost: false,
      carriageBolts: false,
      price: 0,
      roofPitch: newTruss.roofPitch || '5:12',
      overhangType: newTruss.overhangType || 'Standard',
      monoslopeTruss: false,
      connectToHeaderTruss: false,
    };
    setLocalTrusses(prev => ({
      ...prev,
      leanTo: [...prev.leanTo, leanToTruss],
    }));
  };

  const handleUpdateGableTruss = (index: number, trussRequest: TrussRequest) => {
    // Find the truss in local state and update it
    setLocalTrusses(prev => {
      const updated = [...prev.gable];
      const existingTruss = updated[index];
      if (existingTruss) {
        updated[index] = {
          ...existingTruss,
          classification: trussRequest.classification,
          sizeFeet: trussRequest.sizeFeet,
          sizeInches: trussRequest.sizeInches,
          quantity: trussRequest.quantity,
          measurementInsidePost: trussRequest.measurementInsidePost,
          confirmedMeasurementInsidePost: trussRequest.confirmedMeasurementInsidePost,
          roofPitch: trussRequest.roofPitch || '4:12',
          overhangType: trussRequest.overhangType || 'Standard',
          overhangSize: trussRequest.overhangSize,
          carriageBolts: trussRequest.carriageBolts,
          notes: trussRequest.notes,
          price: trussRequest.price,
        };
      }
      return { ...prev, gable: updated };
    });
  };

  const handleUpdateHeaderTruss = (index: number, trussRequest: TrussRequest) => {
    setLocalTrusses(prev => {
      const updated = [...prev.header];
      const existingTruss = updated[index];
      if (existingTruss) {
        updated[index] = {
          ...existingTruss,
          classification: trussRequest.classification,
          sizeFeet: trussRequest.sizeFeet,
          sizeInches: trussRequest.sizeInches,
          quantity: trussRequest.quantity,
          measurementInsidePost: trussRequest.measurementInsidePost,
          confirmedMeasurementInsidePost: trussRequest.confirmedMeasurementInsidePost,
          carriageBolts: trussRequest.carriageBolts,
          notes: trussRequest.notes,
          price: trussRequest.price,
        };
      }
      return { ...prev, header: updated };
    });
  };

  const handleUpdateLeanToTruss = (index: number, trussRequest: TrussRequest) => {
    setLocalTrusses(prev => {
      const updated = [...prev.leanTo];
      const existingTruss = updated[index];
      if (existingTruss) {
        updated[index] = {
          ...existingTruss,
          classification: trussRequest.classification,
          sizeFeet: trussRequest.sizeFeet,
          sizeInches: trussRequest.sizeInches,
          quantity: trussRequest.quantity,
          measurementInsidePost: trussRequest.measurementInsidePost,
          confirmedMeasurementInsidePost: trussRequest.confirmedMeasurementInsidePost,
          roofPitch: trussRequest.roofPitch || '5:12',
          overhangType: trussRequest.overhangType || 'Standard',
          overhangSize: trussRequest.overhangSize,
          carriageBolts: trussRequest.carriageBolts,
          notes: trussRequest.notes,
          monoslopeTruss: trussRequest.monoslopeTruss || false,
          postSizeOverride: trussRequest.postSizeOverride,
          postSizeOverrideValue: trussRequest.postSizeOverrideValue,
          connectToHeaderTruss: trussRequest.connectToHeaderTruss || false,
          price: trussRequest.price,
        };
      }
      return { ...prev, leanTo: updated };
    });
  };

  const handleDeleteGableTruss = (index: number) => {
    setLocalTrusses(prev => ({
      ...prev,
      gable: prev.gable.filter((_, i) => i !== index),
    }));
  };

  const handleDeleteHeaderTruss = (index: number) => {
    setLocalTrusses(prev => ({
      ...prev,
      header: prev.header.filter((_, i) => i !== index),
    }));
  };

  const handleDeleteLeanToTruss = (index: number) => {
    setLocalTrusses(prev => ({
      ...prev,
      leanTo: prev.leanTo.filter((_, i) => i !== index),
    }));
  };

  if (isLoadingOrder || isLoadingBuildings) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
          Order Details: {order?.orderNumber || 'Loading...'}
        </Typography>
        <Button variant="contained" onClick={() => navigate(`/order-details/${orderId}`)}>
          Return to Checkout
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {!isOrderDraft && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This order is no longer in Draft status and cannot be modified. Buildings and trusses are
          read-only.
        </Alert>
      )}

      {/* Row 1: Buildings Carousel (left) + Your Cart Total (right) */}
      <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
        {/* Buildings Section - Left */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Building Selection Area - Header with Add Building Button */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">{buildings.length} Buildings</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddBuildingDialogOpen(true)}
              disabled={!isOrderDraft}
            >
              Add Building
            </Button>
          </Box>

          {/* Building Selection Area - Horizontal Carousel */}
          <BuildingCarousel
            buildings={sortedBuildings}
            selectedBuildingId={selectedBuildingId}
            onBuildingSelect={setSelectedBuildingId}
            onDelete={handleDeleteClick}
            onClone={handleCloneBuilding}
            onUpdateName={isOrderDraft ? handleUpdateBuildingName : undefined}
            isUpdatingName={isUpdatingName}
            isCloningBuilding={isCloningBuilding}
            readOnly={!isOrderDraft}
          />
        </Grid>

        {/* Your Cart Total Section - Right */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Empty header Box to match left side header height */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            {/* Invisible Typography to match left side Typography h6 height */}
            <Typography variant="h6" sx={{ visibility: 'hidden' }}>
              &nbsp;
            </Typography>
            {/* Invisible Button to match left side Add Building button height */}
            <Button variant="contained" size="small" sx={{ visibility: 'hidden' }}>
              &nbsp;
            </Button>
          </Box>
          <BuildingCartSummary
            cartTotal={cartTotal}
            onReturnToCheckout={() => navigate(`/order-details/${orderId}`)}
          />
        </Grid>
      </Grid>

      {/* Per-Building Load Rating Bar */}
      {selectedBuildingId && selectedBuilding && order && selectedBuildingLoadRatings && (
        <BuildingLoadRatingBar
          windLoadMph={selectedBuildingLoadRatings.windLoadMph}
          snowLoadPsf={selectedBuildingLoadRatings.snowLoadPsf}
          isPermanentDwelling={selectedBuilding.isPermanentDwelling}
        />
      )}

      {/* Row 2: Building Information (full width with Building Total inside) */}
      {selectedBuildingId && selectedBuilding && (
        <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
          {/* Building Information Section - Full Width */}
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <BuildingInformationForm
              buildingName={selectedBuilding.name}
              buildingId={selectedBuilding.id}
              existingBuildingNames={sortedBuildings
                .filter(b => b.id !== selectedBuilding.id)
                .map(b => b.name)}
              formData={buildingFormData}
              engineeringFees={engineeringFees}
              onFormDataChange={setBuildingFormData}
              onSave={handleSaveBuildingChanges}
              cartProps={{
                buildingTotal: buildingCartDetails.total,
                gableTotal: buildingCartDetails.gableTotal,
                headerTotal: buildingCartDetails.headerTotal,
                leanToTotal: buildingCartDetails.leanToTotal,
                onUpdateCart: () => handleUpdateCart(),
                onRemoveFromCart: handleRemoveFromCart,
                isSubmitting,
                hasTrussChanges,
              }}
              onUpdateBuildingName={isOrderDraft ? handleUpdateBuildingName : undefined}
              isUpdatingBuildingName={isUpdatingName}
              readOnly={!isOrderDraft}
              formResetKey={formResetKey}
            />
          </Grid>
        </Grid>
      )}

      {/* TODO (UDOMI-502): Integrate TrussSpecificationsPanel when API returns specification data */}
      {/* Component will display cord angle, top/bottom cord lengths, cross tie, drill hole dimensions */}
      {/* Waiting for backend endpoint: GET /api/buildings/{buildingId}/trusses/{trussId}/specification */}

      {/* State Change Confirmation Banner */}
      {selectedBuildingId && selectedBuilding && hasUnconfirmedStateChanges && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="warning"
              size="small"
              variant="outlined"
              onClick={handleConfirmAllStateChanges}
              disabled={isConfirmingAll}
            >
              {isConfirmingAll ? 'Confirming...' : 'Confirm All'}
            </Button>
          }
        >
          The building state has changed. Please confirm the state change for all trusses before
          checking out. Changing the state may affect building specifications, including
          classifications and minimum load ratings.
        </Alert>
      )}

      {/* Row 3+: Full-width Truss Sections */}
      {selectedBuildingId && selectedBuilding && (
        <TrussSectionsContainer
          gableTrusses={localTrusses.gable}
          headerTrusses={localTrusses.header}
          leanToTrusses={localTrusses.leanTo}
          postSize={resolveBuildingPostSizeDisplay(buildingFormData?.postSize)}
          orderDate={order?.orderDate || new Date().toISOString()}
          baySpacing={buildingFormData?.baySpacing ?? 0}
          onAddGableTruss={handleAddGableTruss}
          onAddHeaderTruss={handleAddHeaderTruss}
          onAddLeanToTruss={handleAddLeanToTruss}
          onUpdateGableTruss={handleUpdateGableTruss}
          onUpdateHeaderTruss={handleUpdateHeaderTruss}
          onUpdateLeanToTruss={handleUpdateLeanToTruss}
          onDeleteGableTruss={handleDeleteGableTruss}
          onDeleteHeaderTruss={handleDeleteHeaderTruss}
          onDeleteLeanToTruss={handleDeleteLeanToTruss}
          readOnly={!isOrderDraft}
          onConfirmTrussStateChange={handleConfirmTrussStateChange}
        />
      )}

      {/* Add Building Dialog */}
      <AddBuildingDialog
        open={addBuildingDialogOpen}
        onClose={() => setAddBuildingDialogOpen(false)}
        onSubmit={handleAddBuilding}
        orderId={orderId}
        existingBuildingNames={sortedBuildings.map(b => b.name)}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Remove Building"
        message="Are you sure you want to remove this building?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmColor="error"
      />

      {/* Post Size Warning Dialog */}
      <Dialog
        open={postSizeWarning.show}
        onClose={() => {
          setPostSizeWarning({
            show: false,
            message: '',
            acknowledged: false,
            source: 'buildingSave',
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            color: 'warning.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon />
          Post Size Warning
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {postSizeWarning.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Truss US Engineering recommends 8x8 posts for spans beyond the 6x6 limit for this
            classification. You can proceed with 6x6 posts if you confirm this configuration meets
            your engineering requirements.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPostSizeWarning({
                show: false,
                message: '',
                acknowledged: false,
                source: 'buildingSave',
              });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const retrySource = postSizeWarning.source;
              setPostSizeWarning({
                show: false,
                message: '',
                acknowledged: false,
                source: retrySource,
              });
              if (retrySource === 'updateCart') {
                handleUpdateCart(true);
              } else {
                handleSaveBuildingChanges(undefined, true);
              }
            }}
          >
            Proceed with 6x6 Posts
          </Button>
        </DialogActions>
      </Dialog>

      {/* ASCE Error — Blocking Retry Dialog for Permanent Dwelling toggle */}
      <Dialog open={asceError.show} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          Unable to Retrieve Load Ratings
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to retrieve updated load ratings from ASCE. The Permanent Dwelling change cannot
            be saved until the ASCE service is reachable. Please retry.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setAsceError({ show: false, pendingPermanentDwelling: false, isRetrying: false })
            }
          >
            Cancel Change
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={asceError.isRetrying}
            startIcon={asceError.isRetrying ? <CircularProgress size={18} /> : undefined}
            onClick={async () => {
              if (!order?.jobLocation?.latitude || !order?.jobLocation?.longitude) return;
              setAsceError(prev => ({ ...prev, isRetrying: true }));
              const newRiskLevel = asceError.pendingPermanentDwelling ? 2 : 1;
              try {
                const asceResult = await getAsceLoadRatings({
                  lat: order.jobLocation.latitude,
                  lon: order.jobLocation.longitude,
                  riskLevel: newRiskLevel,
                }).unwrap();
                setAsceError({ show: false, pendingPermanentDwelling: false, isRetrying: false });
                if (selectedBuilding && buildingFormData) {
                  const postSizeEnum =
                    postSizeDisplayToEnum[buildingFormData.postSize] || buildingFormData.postSize;
                  const asceFields = getBuildingAsceFieldsForSave(asceResult, order, newRiskLevel);
                  await updateBuilding({
                    buildingId: selectedBuilding.id,
                    building: {
                      orderId,
                      name: selectedBuilding.name,
                      confirmedLoadRating: selectedBuilding.confirmedLoadRating ?? false,
                      width: buildingFormData.width,
                      length: buildingFormData.length,
                      height: buildingFormData.height,
                      baySpacing: buildingFormData.baySpacing,
                      postSize: postSizeEnum,
                      widthMeasurementReference:
                        buildingFormData.widthMeasurementReference ??
                        WidthMeasurementReference.OutsideOutside,
                      application: buildingFormData.application,
                      additionalNotes: buildingFormData.additionalNotes,
                      isPermanentDwelling: asceError.pendingPermanentDwelling,
                      ...asceFields,
                      trussesProvided: false,
                    },
                  }).unwrap();
                  notifyAsceResultAndPromptIfChanged(
                    asceResult,
                    newRiskLevel,
                    'refreshed',
                    selectedBuilding.id
                  );
                  showToast('Building updated successfully', 'success');
                }
              } catch {
                setAsceError(prev => ({ ...prev, isRetrying: false }));
                showToast('ASCE API is still unavailable. Please try again.', 'error');
              }
            }}
          >
            {asceError.isRetrying ? 'Retrying...' : 'Retry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Order Load Rating Update Dialog */}
      {pendingOrderAsceData && (
        <ConfirmLoadRatingDialog
          open={confirmLoadRatingOpen}
          onClose={() => {
            setConfirmLoadRatingOpen(false);
            setPendingOrderAsceData(null);
          }}
          onConfirm={handleConfirmOrderLoadRatingUpdate}
          previousWindLoad={pendingOrderAsceData.previousWindLoad}
          newWindLoad={pendingOrderAsceData.windLoad}
          previousSnowLoad={pendingOrderAsceData.previousSnowLoad}
          newSnowLoad={pendingOrderAsceData.snowLoad}
        />
      )}

      {/* Unsaved Changes Dialog */}
      <ConfirmDialog
        open={showUnsavedChangesDialog}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        confirmText="Leave"
        cancelText="Stay"
        confirmColor="warning"
      />
    </Box>
  );
};

export default ManageBuildingProjectsPage;
